import React, { useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import styled from 'styled-components';
import ReactStars from 'react-rating-stars-component';
import { toast } from 'react-toastify';

import FirebaseImage from '../../Components/Media/FirebaseImage';
import ProjectImageCarousel from '../../Components/Media/ProjectImageCarousel';
import BeforeAfterComparison from '../../Components/Media/BeforeAfterComparison';
import Modal from '../../Components/UI/Modal/Modal';
import { formatDate } from '../../Utils/date';
import { getClientName, getProfileImage } from '../../Utils/legacyData';
import {
  getPortfolioBeforeAfter,
  getPortfolioMedia,
  getPortfolioSummary,
  getPortfolioTitle,
  PORTFOLIO_MEDIA_SOURCE,
  PORTFOLIO_PHASE,
} from '../../Utils/portfolio';
import { uploadDemandCoverImage } from '../../Utils/uploads';
import { subscribeProjectReview } from '../../Services/trustReviews';

const JobModal = ({
  showModal,
  handleClick,
  demand,
  checkartisan,
  allowContract = false,
  allowCoverUpload = true,
  viewerRole = '',
}) => {
  const history = useHistory();
  const [coverImage, setCoverImage] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [publishedReview, setPublishedReview] = useState(null);

  const opened = showModal === 'open';
  const ownerView = checkartisan === 'false';
  const canViewContract = ownerView || allowContract;
  const canUploadCover = ownerView && allowCoverUpload;
  const contract = demand?.contract;
  const review = publishedReview || (demand?.review?.rating ? { ...demand.review, comment: demand.review.review_text } : null);

  const media = useMemo(() => {
    if (!demand) return [];
    const projectMedia = getPortfolioMedia(demand);
    if (!coverImage || projectMedia.some((item) => item.url === coverImage)) return projectMedia;
    return [{
      id: `artisan-cover-${coverImage}`,
      url: coverImage,
      source: PORTFOLIO_MEDIA_SOURCE.ARTISAN,
      phase: PORTFOLIO_PHASE.AFTER,
    }, ...projectMedia];
  }, [demand, coverImage]);
  const beforeAfter = useMemo(() => getPortfolioBeforeAfter(demand || {}), [demand]);
  const description = getPortfolioSummary(demand || {});
  const publicTitle = getPortfolioTitle(demand || {});
  const showArtisan = viewerRole === 'client';
  const showClient = ownerView || viewerRole === 'artisan';
  const participantName = showArtisan
    ? [contract?.artisan_firstname, contract?.artisan_lastname].filter(Boolean).join(' ') || 'Artisan vérifié'
    : showClient ? getClientName(demand || {}) : 'Client vérifié';
  const participantImage = showArtisan
    ? contract?.artisan_profile_image || ''
    : showClient ? getProfileImage({ profile_image: demand?.client_profile_image, profileImage: demand?.clientProfileImage }) : '';
  const participantLabel = showArtisan ? 'Artisan du projet' : 'Client du projet';
  const participantId = showArtisan ? contract?.artisan_id : demand?.user_id;
  const portfolio = demand?.portfolio || {};

  useEffect(() => {
    setCoverImage('');
    setUploadProgress(0);
  }, [demand?.demand_id, opened]);

  useEffect(() => {
    if (!opened || !demand?.demand_id || !demand?.user_id) { setPublishedReview(null); return undefined; }
    return subscribeProjectReview(
      { demandId: demand.demand_id, reviewerId: demand.user_id },
      (next) => setPublishedReview(next?.status === 'published' ? next : null),
      () => setPublishedReview(null),
    );
  }, [demand?.demand_id, demand?.user_id, opened]);

  const close = () => {
    if (!uploading) handleClick?.();
  };

  const openContract = () => {
    if (!demand || !contract) return;
    close();
    history.push(`/contract?demandId=${encodeURIComponent(demand.demand_id)}`, { demandToPrint: demand });
  };

  const openParticipant = () => {
    if (!participantId) return;
    close();
    if (showArtisan) history.push('/artisanboutique', { artisanId: participantId });
    else if (showClient) history.push('/clientprofile', { clientId: participantId, fromDemandId: demand?.demand_id });
  };

  const handleCoverPhoto = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file || !demand?.demand_id || uploading) return;

    setUploading(true);
    setUploadProgress(0);
    try {
      const downloadURL = await uploadDemandCoverImage(file, demand.demand_id, setUploadProgress);
      setCoverImage(downloadURL);
      toast.success('La photo de couverture a été mise à jour.');
    } catch (error) {
      toast.error(error?.message || 'Le téléchargement de la photo a échoué.');
    } finally {
      setUploading(false);
    }
  };

  return (
    <Modal opened={opened} close={close}>
      <Header>
        <div>
          <Eyebrow>Réalisation</Eyebrow>
          <Title>{demand ? publicTitle.slice(0, 90) : 'Détail du projet'}</Title>
        </div>
        <CloseButton type="button" aria-label="Fermer" onClick={close}>×</CloseButton>
      </Header>

      {!demand ? (
        <EmptyState>Ce projet n’est plus disponible.</EmptyState>
      ) : (
        <>
          <ProjectMeta>
            <Client type="button" onClick={openParticipant} disabled={!participantId || (!showArtisan && !showClient)}>
              <Avatar src={participantImage} fallbackSrc="/images/user.svg" alt="" />
              <div>
                <span>{participantLabel}{participantId && (showArtisan || showClient) ? ' · Voir le profil' : ''}</span>
                <strong>{participantName}</strong>
              </div>
            </Client>
            <DateBadge>Publié le {formatDate(portfolio.published_at || demand.date)}</DateBadge>
          </ProjectMeta>

          {beforeAfter && <BeforeAfterComparison comparison={beforeAfter} />}

          {media.length > 0 ? (
            <ModalCarousel
              images={media}
              fallbackSrc="/images/project-placeholder.svg"
              alt="Réalisation du chantier"
              showMetadata={Boolean(demand?.portfolio?.media)}
            />
          ) : (
            <NoImage>Aucune photo n’a été publiée pour ce projet.</NoImage>
          )}

          <Description>{description || 'Aucune description publique n’a été ajoutée.'}</Description>

          {(portfolio.category || portfolio.city || portfolio.duration || portfolio.price_range) && (
            <PortfolioMeta>
              {portfolio.category && <span>{portfolio.category}</span>}
              {portfolio.city && <span>{portfolio.city}</span>}
              {portfolio.duration && <span>{portfolio.duration}</span>}
              {portfolio.price_range && <span>{portfolio.price_range}</span>}
            </PortfolioMeta>
          )}

          {(portfolio.techniques || portfolio.result) && (
            <ProjectDetails>
              {portfolio.techniques && <div><span>Techniques et matériaux</span><p>{portfolio.techniques}</p></div>}
              {portfolio.result && <div><span>Résultat obtenu</span><p>{portfolio.result}</p></div>}
            </ProjectDetails>
          )}

          {review ? (
            <ReviewCard>
              <ReviewHeader>
                <div>
                  <span>Avis du client</span>
                  <strong>{Number(review.rating) || 0}/5</strong>
                </div>
                <ReactStars
                  count={5}
                  value={Number(review.rating) || 0}
                  size={24}
                  edit={false}
                  isHalf={false}
                  activeColor="#c0a375"
                />
              </ReviewHeader>
              <p>{review.comment || review.review_text || 'Aucun commentaire rédigé.'}</p>
            </ReviewCard>
          ) : (
            <ReviewCard><p>Ce projet n’a pas encore reçu d’avis.</p></ReviewCard>
          )}

          {canUploadCover && (
            <OwnerTools>
              <div>
                <strong>Mettre en valeur ce projet</strong>
                <span>Choisissez une photo JPG, PNG ou WebP de moins de 8 Mo.</span>
              </div>
              <UploadLabel $disabled={uploading}>
                <input type="file" accept="image/jpeg,image/png,image/webp" disabled={uploading} onChange={handleCoverPhoto} />
                {uploading ? `Téléchargement ${uploadProgress}%` : 'Changer la couverture'}
              </UploadLabel>
            </OwnerTools>
          )}

          <Actions>
            <SecondaryButton type="button" onClick={close} disabled={uploading}>Fermer</SecondaryButton>
            {canViewContract && contract && (
              <PrimaryButton type="button" onClick={openContract} disabled={uploading}>Contrat · Imprimer / PDF</PrimaryButton>
            )}
          </Actions>
        </>
      )}
    </Modal>
  );
};

const Header = styled.header`
  display: flex; align-items: flex-start; justify-content: space-between; gap: 1rem; margin-bottom: 1.2rem;
`;
const Eyebrow = styled.div`
  margin-bottom: 0.35rem; color: #a88a5d; font-size: 0.78rem; font-weight: 900; letter-spacing: 0.12em; text-transform: uppercase;
`;
const Title = styled.h2`margin: 0; color: #111111; font-size: clamp(1.25rem, 4vw, 1.8rem); line-height: 1.25;`;
const CloseButton = styled.button`
  width: 2.5rem; height: 2.5rem; flex: 0 0 auto; border: 0; border-radius: 50%; background: #f1ece5; color: #111111; font: inherit; font-size: 1.6rem; cursor: pointer;
`;
const ProjectMeta = styled.div`
  display: flex; align-items: center; justify-content: space-between; gap: 1rem; margin-bottom: 1rem;
  @media (max-width: 520px) { align-items: flex-start; flex-direction: column; }
`;
const Client = styled.button`
  border:0;background:transparent;padding:0;color:inherit;font:inherit;text-align:left;cursor:pointer;&:disabled{cursor:default;}
  display: flex; align-items: center; gap: 0.7rem;
  div { display: grid; gap: 0.1rem; }
  span { color: #6c7983; font-size: 0.74rem; text-transform: uppercase; }
`;
const Avatar = styled(FirebaseImage)`width: 2.8rem; height: 2.8rem; border-radius: 50%; object-fit: cover; background: #edf1f3;`;
const DateBadge = styled.span`padding: 0.45rem 0.75rem; border-radius: 999px; background: #f1f5f7; color: #586975; font-size: 0.8rem;`;
const ModalCarousel = styled(ProjectImageCarousel)`
  min-height: 15rem;
  margin-top: 1rem;
  border-radius: 1rem;
  aspect-ratio: 16 / 9;

  @media (max-width: 42rem) {
    min-height: 13rem;
    border-radius: 0.85rem;
  }
`;
const NoImage = styled.div`padding: 2rem; border-radius: 1rem; background: #faf8f4; color: #687681; text-align: center;`;
const Description = styled.p`margin: 1rem 0 0; padding: 1rem; border-radius: 0.9rem; background: #faf8f4; color: #20384b; white-space: pre-wrap; line-height: 1.6;`;
const PortfolioMeta = styled.div`display: flex; flex-wrap: wrap; gap: .45rem; margin-top: .8rem; span { padding: .35rem .55rem; border-radius: 999px; background: #f3eee6; color: #526875; font-size: .75rem; font-weight: 800; }`;
const ProjectDetails = styled.section`display: grid; grid-template-columns: repeat(2,minmax(0,1fr)); gap: .7rem; margin-top: .8rem; div { padding: .85rem; border-radius: .85rem; background: #f7f9fa; } span { color: #6d7b85; font-size: .7rem; font-weight: 850; text-transform: uppercase; } p { margin: .35rem 0 0; color: #253e51; line-height: 1.5; } @media (max-width: 34rem) { grid-template-columns: 1fr; }`;
const ReviewCard = styled.section`
  margin-top: 1rem; padding: 1rem; border: 1px solid #e5e1da; border-radius: 1rem; color: #20384b;
  p { margin: 0.6rem 0 0; white-space: pre-wrap; }
`;
const ReviewHeader = styled.div`
  display: flex; align-items: center; justify-content: space-between; gap: 1rem;
  div { display: grid; gap: 0.1rem; }
  span { color: #6c7983; font-size: 0.74rem; text-transform: uppercase; }
`;
const OwnerTools = styled.section`
  display: flex; align-items: center; justify-content: space-between; gap: 1rem; margin-top: 1rem; padding: 1rem; border-radius: 1rem; background: #fff8e9;
  div { display: grid; gap: 0.2rem; }
  span { color: #76633b; font-size: 0.8rem; }
  @media (max-width: 580px) { align-items: stretch; flex-direction: column; }
`;
const UploadLabel = styled.label`
  display: inline-flex; align-items: center; justify-content: center; min-height: 2.7rem; padding: 0.65rem 0.9rem; border-radius: 999px; background: #fff; color: #6f5017; font-weight: 800; cursor: ${({ $disabled }) => ($disabled ? 'wait' : 'pointer')}; opacity: ${({ $disabled }) => ($disabled ? 0.65 : 1)};
  input { position: absolute; width: 1px; height: 1px; opacity: 0; pointer-events: none; }
`;
const Actions = styled.div`
  display: flex; flex-wrap: wrap; justify-content: flex-end; gap: 0.7rem; margin-top: 1.25rem;
  @media (max-width: 520px) { flex-direction: column-reverse; }
`;
const BaseButton = styled.button`
  min-height: 2.8rem; padding: 0.72rem 1.05rem; border-radius: 999px; font: inherit; font-weight: 800; cursor: pointer;
  &:disabled { opacity: 0.55; cursor: wait; }
`;
const PrimaryButton = styled(BaseButton)`border: 1px solid #c0a375; background: #c0a375; color: #111111;`;
const SecondaryButton = styled(BaseButton)`border: 1px solid #e7dfd2; background: #fff; color: #111111;`;
const EmptyState = styled.p`margin: 0; padding: 1rem; border-radius: 0.8rem; background: #faf8f4; color: #66615b;`;


export default JobModal;
